import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { HtmlAnchor } from '@helpcenter/analytics-components/src/html-anchor';
import { Link } from '@helpcenter/analytics-components/src/link';
import { ExternalThemedLink } from '@helpcenter/external-themed-link';
import * as fonts from '@helpcenter/styles/src/fonts';
import { ThemedLink } from '@helpcenter/themed-link';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIsCSMTypographyMigrationEnabled } from '@atlassian/help-center-common-util/enable-typography-migration';

interface Props {
    to: string;
    name: string | undefined; // If the name is not available then the crumb (and all its children) will not render
    isFirstCrumb?: boolean;
    actionSubjectId: string;
    testId?: string;
    disableTheme?: boolean;
    tabIndex?: number;
    onClick?: () => void;
}

interface ExternalLinkProps {
    tabIndex?: number;
    href: string;
    'data-test-id'?: string;
    'aria-current'?: boolean | 'time' | 'true' | 'false' | 'page' | 'step' | 'location' | 'date' | undefined;
    actionSubjectId: string;
    onClick?: () => void;
}

/**
 * This is a nestable bread crumb component, it will render the first component with a valid name.
 * It itself will also not render if the name is not valid.
 */
const BreadCrumbDumb: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
    name,
    to,
    children,
    isFirstCrumb,
    actionSubjectId,
    testId,
    tabIndex,
    onClick,
    disableTheme = false,
}) => {
    const isCSMTypographyMigrationEnabled = useIsCSMTypographyMigrationEnabled();
    if (!name) {
        return null;
    }

    let child: React.ReactElement<Props> | undefined;
    React.Children.forEach(children, (element: React.ReactElement<Props>) => {
        // Short circuit if there is already a match
        if (child) {
            return;
        }

        if (element && React.isValidElement(element)) {
            const { name: childName } = element.props;

            if (childName) {
                child = element;
            }
        }
    });
    let linkComponent;
    if (to.startsWith('/')) {
        let RootLinkComponent = disableTheme ? RootLinkDisabledTheme : RootLink;
        if (isCSMTypographyMigrationEnabled) {
            RootLinkComponent = disableTheme ? RootLinkDisabledTheme : RootLinkWithToken;
        }
        linkComponent = (
            <RootLinkComponent
                to={to}
                actionSubjectId={actionSubjectId}
                data-test-id={testId}
                aria-current={child ? 'false' : 'page'}
                tabIndex={tabIndex}
                onClick={onClick}
            >
                {name}
            </RootLinkComponent>
        );
    } else {
        let ExternalLinkComponent = disableTheme ? ExternalLinkDisabledTheme : ExternalLink;
        if (isCSMTypographyMigrationEnabled) {
            ExternalLinkComponent = disableTheme ? ExternalLinkDisabledTheme : ExternalLinkWithToken;
        }
        const externalLinkProps: ExternalLinkProps = {
            tabIndex,
            actionSubjectId,
            onClick,
            href: to,
            'data-test-id': testId,
        };
        if (FeatureGates.checkGate('add-aria-current-to-breadcrumbs')) {
            externalLinkProps['aria-current'] = child ? false : 'page';
        }

        linkComponent = <ExternalLinkComponent {...externalLinkProps}>{name}</ExternalLinkComponent>;
    }
    return (
        <>
            <Box as={'li'} xcss={[breadCrumbContainer, isFirstCrumb ? undefined : breadCrumbSeparator]}>
                {linkComponent}
            </Box>
            {child}
        </>
    );
};

export default BreadCrumbDumb;

const breadCrumbContainer = xcss({
    display: 'flex',
    listStyle: 'none',
    alignItems: 'center',
    margin: 'space.0',
});

const breadCrumbSeparator = xcss({
    '::before': {
        width: '8px',
        flexShrink: 0,
        content: '"/"',
        paddingBlock: 'space.0',
        paddingInline: 'space.075',
        textAlign: 'center',
        lineHeight: '1.5em',
        color: 'color.text',
    },
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const RootLink = styled(ThemedLink)`
    ${fonts.h200};
    margin: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const RootLinkWithToken = styled(ThemedLink)`
    font: ${token(
        'font.body',
        'normal 400 0.875rem/1.25rem ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif'
    )};
    margin: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ExternalLink = styled(ExternalThemedLink)`
    ${fonts.h200};
    margin: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ExternalLinkWithToken = styled(ExternalThemedLink)`
    font: ${token(
        'font.body',
        'normal 400 0.875rem/1.25rem ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif'
    )};
    margin: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const RootLinkDisabledTheme = styled(Link)`
    margin: 0;
    /* stylelint-disable scale-unlimited/declaration-strict-value */
    color: unset !important;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const ExternalLinkDisabledTheme = styled(HtmlAnchor)`
    margin: 0;
    color: unset !important;
`;
